import './antTabs.less';

import React, { forwardRef, useCallback, useState, DragEvent, useMemo } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import b_ from 'b_';

import { Tabs, TabsProps } from 'antd';
import { insertAfter } from 'tools/helpers/array';
import { makeAutoObservable } from 'mobx';
import { useEffect } from 'react';
import { DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { NavigationStore } from 'framework/navigationStore';
import { AntButton } from 'controls/react/ant/antButton';

type OnOrderChangeCallback = (newOrder: React.Key[]) => void;

export type AntTab = TabsProps['items'][0];

export interface AntTabsProps extends Omit<
	TabsProps,
	'destroyInactiveTabPane' | 'tabPosition' | 'indicatorSize' | 'popupClassName'
> {
	draggable?: boolean;
	onOrderChange?: OnOrderChangeCallback;
	fit?: boolean;
	urlKey?: string;
	editable?: boolean;
	stickyNav?: boolean;
}

const b = b_.with('ant-tabs-wrapper');

interface DraggableTabNodeProps extends React.HTMLAttributes<HTMLDivElement> {
	idKey: React.Key;
}

const DraggableTabNode = observer(
	forwardRef<HTMLDivElement, DraggableTabNodeProps>(({ idKey, children }, ref) => {
		const [dragging, setDragging] = useState(false);

		const onDragStart = useCallback(
			(e: DragEvent) => {
				setDragging(true);
				e.dataTransfer.effectAllowed = 'move';
				e.dataTransfer.setData('application/json', JSON.stringify({ idKey }));
				e.stopPropagation();
				return false;
			},
			[idKey],
		);

		const onDragOver = useCallback((e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}, []);

		const onDragEnd = useCallback((e: DragEvent) => {
			setDragging(false);
			e.stopPropagation();
			e.preventDefault();
			return false;
		}, []);

		return (
			<div
				ref={ref}
				onDragStart={onDragStart}
				onDragOver={onDragOver}
				onDragEnd={onDragEnd}
				draggable="true"
				className={b('tab-wrapper', { dragging })}
			>
				{children}
			</div>
		);
	}),
);

interface DraggableTabNavListWrapperProps {
	store: TabsStore;
	children: React.ReactNode;
}

const DraggableTabNavListWrapper = observer(
	forwardRef<HTMLDivElement, DraggableTabNavListWrapperProps>(({ children, store }, ref) => {
		const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
		}, []);

		return (
			<div onDrop={store.onDrop} onDragOver={onDragOver} className={b('tab-nav-list-wrapper')} ref={ref}>
				{children}
			</div>
		);
	}),
);

class TabsStore {
	centers: Map<React.Key, number> = new Map();
	order: React.Key[] = [];
	tabNavList: HTMLDivElement;
	onOrderChange: OnOrderChangeCallback;

	constructor() {
		makeAutoObservable(this);
	}

	setWidth = (key: React.Key, child: HTMLDivElement) => {
		if (child == null) {
			return;
		}
		const left = child.offsetLeft;
		const width = child.offsetWidth;
		const center = left + width / 2;
		this.centers.set(key, center);
	};

	onDrop = (e: DragEvent<HTMLDivElement>) => {
		const data = JSON.parse(e.dataTransfer.getData('application/json')) as { idKey: string };
		const { idKey } = data;
		// HACK: for scrollable nav-list
		const navListRect = this.tabNavList.querySelector('.ant-tabs-nav-list').getBoundingClientRect();
		const x = e.clientX - navListRect.x;

		const afterCenter = this.sortedCenters()
			.reverse()
			.find((c) => c < x);
		const afterId = this.getKeyByCenter(afterCenter);
		const oldOrder = [...this.currentOrder()];
		const newOrder = insertAfter(oldOrder, idKey.toString(), afterId);
		this.onOrderChange?.(newOrder);
	};

	setOnOrderChange(fn: OnOrderChangeCallback) {
		this.onOrderChange = fn;
	}

	currentOrder() {
		return this.sortedCenters().map(this.getKeyByCenter);
	}

	sortedCenters() {
		return [...this.centers.values()].sort((a, b) => a - b);
	}

	getKeyByCenter = (c: number) => {
		for (const [k, v] of this.centers.entries()) {
			if (Math.abs(v - c) < 1) {
				return k;
			}
		}
	};

	setTabNavList(tabNavList: HTMLDivElement) {
		this.tabNavList = tabNavList;
	}
}

const DraggableTabs: React.FC<{
	children?: React.ReactNode;
	onOrderChange: OnOrderChangeCallback;
	className: string;
	items?: TabsProps['items'];
}> = (props) => {
	const [store] = useState(new TabsStore());
	const { children, onOrderChange, items, ...rest } = props;
	useEffect(() => {
		store.setOnOrderChange(onOrderChange);
	}, [onOrderChange]);

	const renderTabBar: TabsProps['renderTabBar'] = (tabBarProps, TabNavList) => {
		return (
			<DraggableTabNavListWrapper store={store} ref={(ref) => store.setTabNavList(ref)}>
				<TabNavList {...tabBarProps}>
					{(node) => (
						<DraggableTabNode key={node.key} idKey={node.key} ref={(child) => store.setWidth(node.key, child)}>
							{node}
						</DraggableTabNode>
					)}
				</TabNavList>
			</DraggableTabNavListWrapper>
		);
	};

	return (
		<Tabs renderTabBar={renderTabBar} items={items} {...rest}>
			{children}
		</Tabs>
	);
};

export const AntTabs = (props: AntTabsProps) => {
	props = useSaveTabToUrl(props);

	const { className, draggable, onOrderChange, fit, editable, stickyNav, items, ...rest } = props;

	let classNameActual = classnames(
		className,
		b({
			'fit': fit,
			'sticky-nav': stickyNav,
		}),
	);

	const itemsActual = useMemo(() => {
		if (!items || !editable) {
			return items;
		}

		return [
			...items.map((x) => ({
				...x,
				label: (
					<div className={b('editable-tab-header')} key={x.key}>
						{x.label}
						{x.closable !== false && (
							<AntButton
								onClick={(e) => {
									e.stopPropagation();
									props.onEdit?.(x.key, 'remove');
								}}
								icon={<DeleteOutlined />}
								size={'small'}
								type={'text'}
							/>
						)}
					</div>
				),
			})),
			{
				key: 'add-button',
				label: (
					<AntButton
						onClick={(ev: React.MouseEvent) => props.onEdit?.(ev, 'add')}
						icon={<PlusOutlined />}
						type={'text'}
					/>
				),
				children: '',
				disabled: true,
			},
		];
	}, [editable, items]);

	rest.moreIcon ||= <MoreOutlined />;

	if (draggable) {
		classNameActual = classnames(classNameActual, b({ draggable: true }));
		return <DraggableTabs className={classNameActual} onOrderChange={onOrderChange} items={itemsActual} {...rest} />;
	}
	return <Tabs className={classNameActual} items={itemsActual} {...rest} />;
};

export function useSaveTabToUrl(props: AntTabsProps): AntTabsProps {
	const { onChange, urlKey, ...rest } = props;

	const onChangeWrapper = useCallback(
		(tabKey: string) => {
			if (urlKey) {
				NavigationStore.updateCurrentItemUrl({
					[urlKey]: tabKey,
				});
			}
			onChange?.(tabKey);
		},
		[onChange],
	);

	return {
		...rest,
		onChange: onChangeWrapper,
	};
}
